/** Translator, passed in: these messages are read on the connect form. */
export type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Facebook ad-account validation.
 * Confirms a long-lived token can read a given ad account before we save it.
 * Client-side only — no app secret involved, just the user's own token.
 */

const API_VERSION = "v19.0";

export interface FbAccountValidation {
  valid: boolean;
  accountName?: string;
  accountStatus?: number;
  error?: string;
}

function normalizeActId(accountId: string): string {
  return accountId.startsWith("act_") ? accountId : `act_${accountId}`;
}

function translateError(code: number | undefined, message: string, t: TFn): string {
  if (code === 190) return t("settings.tokenInvalid");
  if (code === 100 || code === 200 || code === 803) {
    return t("settings.noAccountAccess");
  }
  return `Facebook: ${message}`;
}

export async function validateFacebookAdAccount(
  accountId: string,
  token: string,
  // Unused: Facebook has no second credential field. The parameter is here so
  // every validator shares one signature, which is what lets manualMethods
  // hold them in a single typed table.
  _secret: string | undefined,
  t: TFn,
): Promise<FbAccountValidation> {
  const actId = normalizeActId(accountId);
  const url =
    `https://graph.facebook.com/${API_VERSION}/${actId}` +
    `?fields=name,account_status,currency,timezone_name`;

  try {
    // Bearer header, not ?access_token=. This call is made from the browser, so
    // the query string lands in devtools, in the performance timeline, and in
    // anything that records resource URLs — a place a long-lived ads token has
    // no business being. Graph accepts either; verified against a live account
    // that both forms return the same body.
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const data = await resp.json();
    if (data.error) {
      return { valid: false, error: translateError(data.error.code, data.error.message, t) };
    }
    return { valid: true, accountName: data.name, accountStatus: data.account_status };
  } catch {
    return { valid: false, error: t("settings.connectFailed", { platform: "Facebook" }) };
  }
}
